import { Form, FormikProvider, useFormik } from 'formik';
import GlobalDialog from '../../../components/mui/Dialogue/GlobalDialog';
import { Box, Stack, TextField, MenuItem, CircularProgress, Button } from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import * as Yup from 'yup';
import dayjs from 'dayjs';
import { useSnackbar } from 'notistack';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { Icon } from '@iconify/react';
import { useDispatch } from 'react-redux';
import { useSelector } from 'react-redux';
import { addLogs, getFeeForecastData, overrideForecastAmount } from '../../../redux/features/feeForecastSlice';
import useHandleApiResponse from '../../../Hooks/useHandleApiResponse';
import { useEffect, useState } from 'react';

const AddForecastLogs = ({ open, setOpen, enrollId, setEnrollId, finalSessions, dispatchParams }) => {
  const { enqueueSnackbar } = useSnackbar();
  const dispatch = useDispatch();
  const { loading, feeForecast } = useSelector((state) => state.feeForecast);
  const [submitting, setSubmitting] = useState(false);
  const meta = feeForecast?.meta;
  const handleApiResponse = useHandleApiResponse();

  const profile = JSON.parse(localStorage.getItem('cred')).profile;
  const isFinanceOrAudit = profile.departmentId == 7 || profile?.departmentId == 33;
  const isCRM = meta?.allowedFilters?.includes('cro') && !meta?.allowedFilters?.includes('crm');

  const Schema = Yup.object().shape({
    amountToCollect: Yup.string().required('Amount To Collect is required'),
    overrideReason: isCRM ? Yup.string().required('Reason for override is required') : Yup.string(),
  });

  const formik = useFormik({
    initialValues: {
      courseEnrollmentId: '',
      month: dayjs().format('MM'),
      year: dayjs().format('YYYY'),
      expectedSessions: '',
      amountToCollect: '',
      overrideReason: '',
    },
    validationSchema: Schema,
    onSubmit: (values, { resetForm }) => {
      setSubmitting(true);
      const actionToDispatch = overrideForecastAmount({
        courseEnrollmentId: values.courseEnrollmentId,
        month: Number(values.month),
        year: Number(values.year),
        finalAmount: Number(values.amountToCollect),
        overrideReason: values.overrideReason || ''
      });

      dispatch(actionToDispatch).then((res) => {
        handleApiResponse(res, {
          onSuccess: (responsePayload) => {
            const successMsg = responsePayload?.message || 'Action completed successfully!';
            enqueueSnackbar(successMsg, { variant: 'success' });
            setOpen(false);
            setEnrollId(null);
            resetForm();
            dispatch(getFeeForecastData(dispatchParams));
          },
          onFailed: () => {
            console.log('Something went wrong');
          },
        });
        setSubmitting(false);
      }).catch((err) => {
        console.error(err);
        setSubmitting(false);
      });
    },
  });
  useEffect(() => {
    if (open) {
      formik.setFieldValue('courseEnrollmentId', enrollId);
      formik.setFieldValue('expectedSessions', finalSessions);
      formik.setFieldValue('month', dayjs(dispatchParams.date).format('M'));
      formik.setFieldValue('year', dayjs(dispatchParams.date).format('YYYY'));
    } else {
      formik.setFieldValue('expectedSessions', '');
      formik.setFieldValue('overrideReason', '');
    }
  }, [open]);

  // const methodOptions = [
  //   { id: 1, name: 'Option' },
  //   { id: 2, name: 'Percentage' },
  // ];

  return (
    <GlobalDialog
      title={'Add Forecast Log'}
      open={open}
      setOpen={setOpen}
      maxWidth={'sm'}
      closeButton={true}
      fullWidth
      content={
        <>
          {open ? (
            <FormikProvider value={formik}>
              <Form onSubmit={formik.handleSubmit}>
                <Box>
                  <Stack spacing={2}>
                    {/* Month & Year Picker */}
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <DatePicker
                        views={['year', 'month']}
                        label="Select Month & Year"
                        value={
                          formik.values.month && formik.values.year
                            ? dayjs(`${formik.values.year}-${formik.values.month}-01`)
                            : dayjs()
                        }
                        onChange={(value) => {
                          if (value) {
                            formik.setFieldValue('month', value.month() + 1);
                            formik.setFieldValue('year', value.year());
                          }
                        }}
                        slotProps={{
                          textField: {
                            fullWidth: true,
                          },
                        }}
                      />
                    </LocalizationProvider>

                    {/* Expected Sessions */}
                    <TextField
                      label="Expected Sessions"
                      name="expectedSessions"
                      disabled
                      type="number"
                      fullWidth
                      value={formik.values.expectedSessions}
                      onChange={formik.handleChange}
                      // error={formik.touched.expectedSessions && Boolean(formik.errors.expectedSessions)}
                      // helperText={formik.touched.expectedSessions && formik.errors.expectedSessions}
                    />

                    {/* Forecast Calculated Method */}
                    {/* <TextField
                      select
                      label="Forecast Calculated Method"
                      name="forcastCalculatedMethod"
                      fullWidth
                      value={formik.values.forcastCalculatedMethod}
                      onChange={formik.handleChange}
                      error={formik.touched.forcastCalculatedMethod && Boolean(formik.errors.forcastCalculatedMethod)}
                      helperText={formik.touched.forcastCalculatedMethod && formik.errors.forcastCalculatedMethod}
                    >
                      {methodOptions.map((item) => (
                        <MenuItem key={item.id} value={item.id}>
                          {item.name}
                        </MenuItem>
                      ))}
                    </TextField> */}

                    {/* Amount To Collect */}
                    <TextField
                      label="Amount To Collect"
                      name="amountToCollect"
                      type="number"
                      fullWidth
                      value={formik.values.amountToCollect}
                      onChange={formik.handleChange}
                      error={formik.touched.amountToCollect && Boolean(formik.errors.amountToCollect)}
                      helperText={formik.touched.amountToCollect && formik.errors.amountToCollect}
                    />

                    {/* Override Reason */}
                    <TextField
                      label="Reason for Override"
                      name="overrideReason"
                      multiline
                      rows={2}
                      fullWidth
                      value={formik.values.overrideReason}
                      onChange={formik.handleChange}
                      error={formik.touched.overrideReason && Boolean(formik.errors.overrideReason)}
                      helperText={formik.touched.overrideReason && formik.errors.overrideReason}
                      placeholder="Enter the reason for manual override edit..."
                    />
                  </Stack>
                  <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                    <Button
                      variant="contained"
                      disabled={submitting}
                      size="small"
                      type="submit"
                      sx={{
                        backgroundColor: '#eff4f9',
                        color: '#3173b4',
                        fontWeight: 'bold',
                        boxShadow: 'none',
                        textTransform: 'none',
                        px: 3,
                        py: 0.8,
                        '&:hover': {
                          backgroundColor: '#e0e7ff',
                          boxShadow: 'none',
                        },
                        '&:disabled': {
                          backgroundColor: '#eff4f9',
                          color: '#3173b4',
                          opacity: 0.7,
                        }
                      }}
                      startIcon={
                        submitting ? (
                          <CircularProgress size={16} color="inherit" />
                        ) : (
                          <Icon icon="mingcute:add-line" width="20" height="20" />
                        )
                      }
                    >
                      {submitting ? 'Please Wait...' : (isCRM ? 'Submit Override Request' : 'Add Log')}
                    </Button>
                  </Box>
                </Box>
              </Form>
            </FormikProvider>
          ) : null}
        </>
      }
    />
  );
};

export default AddForecastLogs;
